import styles from "./style.postcss";

import React, { Component } from "react";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";

import is from "is_js";
import ReactGA from "react-ga4";
import ViewNavBig from "./ViewNavBig";
import ViewNavSmall from "./ViewNavSmall";
import ViewNavButton from "./ViewNavButton";
import ViewNavExtra from "./ViewNavExtra";
import { connect, mediaQuery, log, Icon, testClass } from "omni-common-ui";
import { Map } from "immutable";
import { actions as uiStateAction } from "data/UiState";
import classnames from "classnames";
import PropTypes from "prop-types";

const EDIT_MODE_MATCH_REGEXP = new RegExp("^edit$", "i");

// ViewNav per-route settings may be supplied as functions
export const fnToValue = (any, props) => {
  if (is.function(any)) {
    return any(props);
  }
  return any;
};

export class ViewNav extends Component {
  constructor(props) {
    super(props);
    this._queryListener = this._queryListener.bind(this);
    this._query = mediaQuery("(min-width: 639px)");
    this._setButtonsToDisplay(this.props);
    this.state = { big: this._query.matches };
  }

  componentDidMount() {
    this._query.addListener(this._queryListener);
  }

  componentWillReceiveProps(props) {
    this._setButtonsToDisplay(props);
  }

  componentWillUnmount() {
    this._query.removeListener(this._queryListener);
  }

  _queryListener(mql) {
    this.setState({ big: mql.matches });
  }

  _setButtonsToDisplay(props) {
    const currentRoute = ViewNav.getCurrentRouteSegment(props);
    const currentModeName =
      props.params && props.params.mode && props.params.mode.length
        ? props.params.mode
        : "default";

    // do not show action buttons in `edit` mode - the save bar does that for us
    if (EDIT_MODE_MATCH_REGEXP.test(currentModeName)) {
      this._buttons = new Map();
      return;
    }

    const actionButtonsMap = {};
    let actionButtonsForMode;
    if (
      currentRoute.actionButtons &&
      currentRoute.actionButtons[currentModeName]
    ) {
      actionButtonsForMode = currentRoute.actionButtons[currentModeName];
      Object.assign(actionButtonsMap, actionButtonsForMode);
    }

    const buttonsToDisplay = Object.keys(actionButtonsMap).filter(
      (buttonLabel) => {
        const buttonDescriptor = actionButtonsMap[buttonLabel];
        try {
          if (is.function(buttonDescriptor.visibleWhen)) {
            if (buttonDescriptor.visibleWhen.length < 2) {
              return buttonDescriptor.visibleWhen(props);
              // handle the older form of this method where there are two args - state and props
            } else if (!buttonDescriptor.visibleWhen(props.state, props)) {
              return false;
            }
          }
        } catch (err) {
          log.warn(
            `Error in \`visibleWhen\` in route \`${currentRoute.path}\``,
            err,
          );
        }
        return true;
      },
    );

    this._buttons = new Map(actionButtonsMap).filter(
      (v, key) => buttonsToDisplay.indexOf(key) >= 0,
    );
  }

  _getIsDisabled(buttonDescriptor) {
    const props = this.props;
    try {
      if (is.function(buttonDescriptor.disableWhen)) {
        if (buttonDescriptor.disableWhen.length < 2) {
          return buttonDescriptor.disableWhen(props);
          // handle the older form of this method where there are two args - state and props
        } else if (buttonDescriptor.disableWhen(props.state, props)) {
          return true;
        }
      }
    } catch (err) {
      const currentRoute = ViewNav.getCurrentRouteSegment(this.props);
      log.warn(
        `Error in \`disableWhen\` in route \`${currentRoute.path}\``,
        err,
      );
    }
    return false;
  }

  _getOnClickHandler(buttonDescriptor, buttonLabel) {
    if (buttonDescriptor.onClick) {
      return () => {
        registerClick();
        return buttonDescriptor.onClick.call(
          this,
          this.props.state,
          this.props,
        );
      };
    }

    return (
      buttonDescriptor.route &&
      (() => {
        registerClick();
        return this._redirect(fnToValue(buttonDescriptor.route, this.props));
      })
    );

    function registerClick() {
      ReactGA.event({
        category: "Navigation",
        action: "Clicked action button",
        label: `Clicked ${buttonLabel} in the top toolbar`,
      });
    }
  }

  _redirect(param) {
    const { pushRoute, buildRoute } = this.props;
    pushRoute(buildRoute(param));
  }

  _hasButtons() {
    return this._buttons.size > 0;
  }

  _hasExtra() {
    const { sessionNav: settings } =
      ViewNav.getCurrentRouteSegment(this.props) || {};
    if (this.props.showExpandable) {
      return true;
    }
    return (
      this.props.hideExpandable !== true &&
      (!settings || settings.noUnitSessionNavigation !== true)
    );
  }

  _renderExpandIcon() {
    const { showNavigationBar, toggleNavigation } = this.props;

    if (!this._hasExtra()) {
      return null;
    }

    const iconClass = classnames(
      styles.ViewNav_expandIcon,
      { [styles.__reverse]: showNavigationBar },
      { [styles.__sticky]: this.state.stickToTop },
    );

    return (
      <ViewNavExtra>
        <Icon
          id="chevron-fat-down"
          className={iconClass}
          onClick={toggleNavigation}
        />
      </ViewNavExtra>
    );
  }

  _renderButtons() {
    return this._buttons
      .map((buttonDescriptor, buttonLabel) => {
        const title = is.function(buttonDescriptor.label)
          ? buttonDescriptor.label(this.props)
          : buttonLabel;
        const className = testClass(
          `viewnav-button-${title.replace(/\s+/g, "-").toLowerCase()}`,
        );
        // eslint-disable-next-line react/no-array-index-key
        return (
          <ViewNavButton
            key={buttonLabel}
            title={title}
            className={className}
            isPrimary={buttonDescriptor.isPrimary}
            disabled={this._getIsDisabled(buttonDescriptor)}
            onClick={this._getOnClickHandler(buttonDescriptor, buttonLabel)}
            newTab={buttonDescriptor.newTab}
            linkHref={fnToValue(buttonDescriptor.linkHref, this.props)}
          />
        );
      })
      .toArray();
  }

  _renderContent() {
    const ViewNavImpl = this.state.big ? ViewNavBig : ViewNavSmall;
    return (
      <ViewNavImpl>
        {this._renderButtons()}
        {this._renderExpandIcon()}
      </ViewNavImpl>
    );
  }

  render() {
    const currentRoute = ViewNav.getCurrentRouteSegment(this.props);
    if (
      currentRoute &&
      currentRoute.actionButtons &&
      currentRoute.actionButtons.hidden === true
    ) {
      return null;
    }
    if (!this._hasButtons() && !this._hasExtra()) {
      return null;
    }
    const ViewNavImpl = this.state.big ? ViewNavBig : ViewNavSmall;
    return (
      <ViewNavImpl>
        {this._renderButtons()}
        {this._renderExpandIcon()}
      </ViewNavImpl>
    );
  }
}

ViewNav.getCurrentRouteSegment = ({ routes }) =>
  routes ? routes[routes.length - 1] : {};

ViewNav.propTypes = {
  isMarkable: PropTypes.bool,
  state: PropTypes.object,
  routes: PropTypes.array.isRequired,
  location: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  buildRoute: PropTypes.func.isRequired,
  pushRoute: PropTypes.func.isRequired,
  toggleNavigation: PropTypes.func.isRequired,
  showNavigationBar: PropTypes.bool.isRequired,
  hideExpandable: PropTypes.bool,
  showExpandable: PropTypes.bool,
};

export function mapStateToProps(state) {
  const showNavigationBar = uiStateAction.getShowNavigationBar(state);
  return { showNavigationBar };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    { pushRoute: (path) => dispatch(push(path)) },
    bindActionCreators(uiStateAction, dispatch),
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewNav);



// WEBPACK FOOTER //
// ./components/ViewNav/index.jsx